import type { MutableRefObject, RefObject } from 'react';
import { useEffect } from 'react';
import type { ReactFlowInstance } from '@xyflow/react';
import { getAncestorPath, type LayoutResult } from './layout-mindmap';
import { FIT_VIEW_DURATION, FIT_VIEW_PADDING } from './mindmap-constants';
import {
  MINDMAP_FIT_TARGET,
  type PendingMindmapFitState,
} from './mindmap-fit-state';
import type { SelectMindmapNodeInput } from './mindmap-navigation';
import type { MindmapFlowEdge, MindmapFlowNode, ParsedMindmap } from './mindmap-types';

const MIN_REFIT_SIZE = 4;

enum MindmapFitRequestKind {
  All = 'all',
  None = 'none',
  Subtree = 'subtree',
}

type MindmapFitRequest =
  | { kind: MindmapFitRequestKind.All }
  | { kind: MindmapFitRequestKind.None }
  | { kind: MindmapFitRequestKind.Subtree; nodeIds: string[] };

type MindmapFitRequestRunner = (input: {
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge>;
  request: MindmapFitRequest;
}) => void;

const MINDMAP_FIT_REQUEST_RUNNER_BY_KIND: Record<
  MindmapFitRequestKind,
  MindmapFitRequestRunner
> = {
  [MindmapFitRequestKind.All]: ({ reactFlowInstance }) => {
    reactFlowInstance.fitView({
      duration: FIT_VIEW_DURATION,
      padding: FIT_VIEW_PADDING,
    });
  },
  [MindmapFitRequestKind.None]: () => undefined,
  [MindmapFitRequestKind.Subtree]: ({ reactFlowInstance, request }) => {
    if (request.kind !== MindmapFitRequestKind.Subtree) {
      return;
    }

    reactFlowInstance.fitView({
      nodes: request.nodeIds.map((id) => ({ id })),
      duration: FIT_VIEW_DURATION,
      padding: FIT_VIEW_PADDING,
    });
  },
};

interface UseMindmapViewportFitInput {
  didInitialFitRef: MutableRefObject<boolean>;
  layout: LayoutResult | null;
  parentMap: Map<string, string>;
  parsed: ParsedMindmap | null;
  pendingFitRef: MutableRefObject<PendingMindmapFitState>;
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge> | null;
  reactFlowWrapperRef: RefObject<HTMLDivElement | null>;
  selectedNodeId: string | null;
  setSelectedMindmapNode: (input: SelectMindmapNodeInput) => void;
  shouldFitSelectedNodeRef: MutableRefObject<boolean>;
  visibleNodeIds: Set<string>;
}

export function useMindmapViewportFit(input: UseMindmapViewportFitInput): void {
  const {
    didInitialFitRef,
    layout,
    parentMap,
    parsed,
    pendingFitRef,
    reactFlowInstance,
    reactFlowWrapperRef,
    selectedNodeId,
    setSelectedMindmapNode,
    shouldFitSelectedNodeRef,
    visibleNodeIds,
  } = input;

  useEffect(() => {
    return schedulePendingMindmapFit({
      didInitialFitRef,
      layout,
      pendingFitRef,
      reactFlowInstance,
    });
  }, [didInitialFitRef, layout, pendingFitRef, reactFlowInstance]);

  useEffect(() => {
    const wrapper = reactFlowWrapperRef.current;
    if (!wrapper || !reactFlowInstance || typeof ResizeObserver === 'undefined') {
      return;
    }

    let animationFrame = 0;
    const refitVisibleTree = (): void => {
      reactFlowInstance.fitView({
        duration: FIT_VIEW_DURATION,
        padding: FIT_VIEW_PADDING,
      });
    };
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }

      const { width, height } = entry.contentRect;
      if (width <= MIN_REFIT_SIZE || height <= MIN_REFIT_SIZE) {
        return;
      }

      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(refitVisibleTree);
    });

    observer.observe(wrapper);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [reactFlowInstance, reactFlowWrapperRef]);

  useEffect(() => {
    if (!selectedNodeId || !reactFlowInstance || !shouldFitSelectedNodeRef.current) {
      return;
    }

    reactFlowInstance.fitView({
      nodes: [{ id: selectedNodeId }],
      duration: FIT_VIEW_DURATION,
      padding: FIT_VIEW_PADDING,
    });
    shouldFitSelectedNodeRef.current = false;
  }, [reactFlowInstance, selectedNodeId, shouldFitSelectedNodeRef]);

  useEffect(() => {
    const fallbackNodeId = getVisibleSelectionFallbackNodeId({
      layout,
      parentMap,
      parsed,
      selectedNodeId,
      visibleNodeIds,
    });
    if (!fallbackNodeId || typeof window === 'undefined') {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      setSelectedMindmapNode({ nodeId: fallbackNodeId });
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [layout, parentMap, parsed, selectedNodeId, setSelectedMindmapNode, visibleNodeIds]);
}

function applyPendingMindmapFit(input: {
  didInitialFitRef: MutableRefObject<boolean>;
  pendingFitRef: MutableRefObject<PendingMindmapFitState>;
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge>;
}): void {
  const request = getPendingMindmapFitRequest({
    didInitialFit: input.didInitialFitRef.current,
    pendingFit: input.pendingFitRef.current,
  });
  MINDMAP_FIT_REQUEST_RUNNER_BY_KIND[request.kind]({
    reactFlowInstance: input.reactFlowInstance,
    request,
  });
  input.didInitialFitRef.current = true;
  input.pendingFitRef.current = {
    target: MINDMAP_FIT_TARGET.None,
    nodeIds: [],
  };
}

function schedulePendingMindmapFit(input: {
  didInitialFitRef: MutableRefObject<boolean>;
  layout: LayoutResult | null;
  pendingFitRef: MutableRefObject<PendingMindmapFitState>;
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge> | null;
}): (() => void) | undefined {
  const schedulerInput = getPendingFitSchedulerInput(input);
  if (!schedulerInput) {
    return undefined;
  }

  const fitToRequestedTarget = (): void => {
    applyPendingMindmapFit({
      didInitialFitRef: input.didInitialFitRef,
      pendingFitRef: input.pendingFitRef,
      reactFlowInstance: schedulerInput.reactFlowInstance,
    });
  };

  if (typeof window === 'undefined') {
    fitToRequestedTarget();
    return undefined;
  }

  const animationFrame = window.requestAnimationFrame(() => {
    fitToRequestedTarget();
  });

  return () => {
    window.cancelAnimationFrame(animationFrame);
  };
}

function getPendingFitSchedulerInput(input: {
  layout: LayoutResult | null;
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge> | null;
}): {
  layout: LayoutResult;
  reactFlowInstance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge>;
} | null {
  if (!input.reactFlowInstance) {
    return null;
  }

  if (!input.layout) {
    return null;
  }

  if (input.layout.nodes.length === 0) {
    return null;
  }

  return {
    layout: input.layout,
    reactFlowInstance: input.reactFlowInstance,
  };
}

function getPendingMindmapFitRequest(input: {
  didInitialFit: boolean;
  pendingFit: PendingMindmapFitState;
}): MindmapFitRequest {
  if (shouldFitPendingSubtree(input.pendingFit)) {
    return {
      kind: MindmapFitRequestKind.Subtree,
      nodeIds: input.pendingFit.nodeIds,
    };
  }

  if (shouldFitFullTree(input)) {
    return {
      kind: MindmapFitRequestKind.All,
    };
  }

  return {
    kind: MindmapFitRequestKind.None,
  };
}

function shouldFitPendingSubtree(pendingFit: PendingMindmapFitState): boolean {
  if (pendingFit.target !== MINDMAP_FIT_TARGET.Subtree) {
    return false;
  }

  return pendingFit.nodeIds.length > 0;
}

function shouldFitFullTree(input: {
  didInitialFit: boolean;
  pendingFit: PendingMindmapFitState;
}): boolean {
  if (!input.didInitialFit) {
    return true;
  }

  return input.pendingFit.target === MINDMAP_FIT_TARGET.All;
}

function getVisibleSelectionFallbackNodeId(input: {
  layout: LayoutResult | null;
  parentMap: Map<string, string>;
  parsed: ParsedMindmap | null;
  selectedNodeId: string | null;
  visibleNodeIds: Set<string>;
}): string | null {
  const visibleSelection = getVisibleSelectionInput(input);
  if (!visibleSelection) {
    return null;
  }

  if (input.visibleNodeIds.has(visibleSelection.selectedNodeId)) {
    return null;
  }

  const ancestorPath = getAncestorPath({
    nodeId: visibleSelection.selectedNodeId,
    parentMap: input.parentMap,
  });
  return ancestorPath.find((nodeId) => {
    return input.visibleNodeIds.has(nodeId);
  }) ?? visibleSelection.parsed.root.id;
}

function getVisibleSelectionInput(input: {
  layout: LayoutResult | null;
  parsed: ParsedMindmap | null;
  selectedNodeId: string | null;
}): {
  layout: LayoutResult;
  parsed: ParsedMindmap;
  selectedNodeId: string;
} | null {
  if (!input.parsed) {
    return null;
  }

  if (!input.layout) {
    return null;
  }

  if (!input.selectedNodeId) {
    return null;
  }

  return {
    layout: input.layout,
    parsed: input.parsed,
    selectedNodeId: input.selectedNodeId,
  };
}
